// src/utils/act/actPlanEngine.js
//
// Deterministic ACT study-plan engine — mirrors the SAT engine but works over
// FOUR sections (English/Math/Reading/Science) whose plan items are the ACT
// categories (no SAT-style subcategories). Prioritization is math, not AI:
//   1. Within each section, rank categories by lowest accuracy first.
//   2. Split hours across sections weighted by each section's score gap
//      (bigger gap → more hours), falling back to diagnostic Rx need.
//   3. Within a section, fund the weakest categories in whole-hour blocks;
//      the rest become "Not Covered" (stretch) items.
// Reuses the SAT allocator (whole-hour, meaningful blocks).

import { allocateSubjectHours } from "../sat/planEngine";
import { computeComposite } from "../plan/planConfig";
import { ACT_SECTION_ORDER, normalize } from "./actStructure";

export function rankActWeaknesses(breakdown) {
  const sections = (breakdown && breakdown.sections) || {};
  const subjects = [];
  const notTested = [];

  ACT_SECTION_ORDER.forEach((section) => {
    const cats = sections[section];
    if (!cats || !cats.length) return;
    let totalQ = 0;
    let totalC = 0;
    const items = [];
    cats.forEach((c) => {
      if (!c.total) {
        notTested.push({ subject: section, majorCategory: section, subcategory: c.category });
        return;
      }
      totalQ += c.total;
      totalC += c.correct;
      if ((c.rxHours || 0) <= 0) return; // mastered — no recommended hours
      items.push({
        id: `${normalize(section)}:${normalize(c.category)}`,
        subject: section,
        majorCategory: section,
        subcategory: c.category,
        key: normalize(c.category),
        accuracyPct: c.pct,
        total: c.total,
        correct: c.correct,
        multiplier: c.multiplier || 0,
        rxHours: c.rxHours,
      });
    });
    items.sort((a, b) => {
      if (a.accuracyPct !== b.accuracyPct) return a.accuracyPct - b.accuracyPct;
      return b.rxHours - a.rxHours;
    });
    if (items.length || totalQ > 0) {
      subjects.push({
        subject: section,
        accuracyPct: totalQ > 0 ? Math.round((100 * totalC) / totalQ) : null,
        rxHours: items.reduce((a, b) => a + b.rxHours, 0),
        items,
      });
    }
  });

  return { subjects, notTested };
}

// Whole-hour split across N sections using the largest-remainder method.
function splitAcrossSubjects(totalHours, weighted) {
  const res = {};
  weighted.forEach((w) => (res[w.subject] = 0));
  const tested = weighted.filter((w) => w.weight > 0);
  if (totalHours <= 0 || !tested.length) return res;

  const W = tested.reduce((a, b) => a + b.weight, 0);
  const rows = tested.map((w) => {
    const exact = (totalHours * w.weight) / W;
    const base = Math.floor(exact);
    return { subject: w.subject, base, frac: exact - base };
  });
  let assigned = 0;
  rows.forEach((r) => {
    res[r.subject] = r.base;
    assigned += r.base;
  });
  let remainder = Math.round(totalHours) - assigned;
  rows.sort((a, b) => b.frac - a.frac);
  for (let i = 0; i < rows.length && remainder > 0; i += 1) {
    res[rows[i].subject] += 1;
    remainder -= 1;
  }
  return res;
}

export function generateActPlanSkeleton(breakdown, totalHoursInput, options = {}) {
  const totalHours = Math.max(0, Math.round(Number(totalHoursInput) || 0));
  const minBlock = options.minBlock == null ? 1 : options.minBlock;
  const currentBySubject = options.currentBySubject || {};
  const targetBySubject = options.targetBySubject || {};

  const ranked = rankActWeaknesses(breakdown);

  const gapOf = (s) => {
    const c = Number(currentBySubject[s]);
    const t = Number(targetBySubject[s]);
    return Number.isFinite(c) && Number.isFinite(t) ? Math.max(0, t - c) : 0;
  };
  const anyGap = ranked.subjects.some((s) => gapOf(s.subject) > 0);
  const weighted = ranked.subjects.map((s) => ({
    subject: s.subject,
    weight: s.items.length ? (anyGap ? gapOf(s.subject) : s.rxHours) : 0,
  }));
  const split = splitAcrossSubjects(totalHours, weighted);

  const subjectResults = ranked.subjects.map((s) => {
    const alloc = allocateSubjectHours(s.items, split[s.subject] || 0, { minBlock });
    const req = alloc.items.filter((it) => !it.isStretch && it.allocatedHours > 0);
    const str = alloc.items.filter((it) => it.isStretch || it.allocatedHours <= 0);
    return {
      subject: s.subject,
      accuracyPct: s.accuracyPct,
      rxHours: s.rxHours,
      allocatedHours: split[s.subject] || 0,
      requiredCount: req.length,
      stretchCount: str.length,
      requiredHours: req.reduce((a, b) => a + (b.allocatedHours || 0), 0),
      items: alloc.items,
    };
  });

  const required = [];
  const stretch = [];
  subjectResults.forEach((sr) => {
    sr.items.forEach((it, idx) => {
      const entry = { ...it, subjectRank: idx + 1 };
      if (it.isStretch || it.allocatedHours <= 0) stretch.push(entry);
      else required.push(entry);
    });
  });
  const checklist = [...required, ...stretch].map((it, i) => ({
    ...it,
    priority: i + 1,
    status: "todo",
    coveredHours: 0,
    progressPct: 0,
    progressHistory: [],
    completedByUid: null,
    completedByName: null,
    completedAt: null,
    sessionLogId: null,
  }));

  const sections = ranked.subjects.map((s) => s.subject);
  const currentScore = computeComposite("ACT", sections.map((s) => currentBySubject[s]));
  const targetScore = computeComposite("ACT", sections.map((s) => targetBySubject[s]));
  const scoreGap = currentScore != null && targetScore != null ? Math.max(0, targetScore - currentScore) : null;
  const subjectScores = sections.map((s) => ({
    subject: s,
    current: Number.isFinite(Number(currentBySubject[s])) ? Number(currentBySubject[s]) : null,
    target: Number.isFinite(Number(targetBySubject[s])) ? Number(targetBySubject[s]) : null,
  }));

  return {
    type: "ACT",
    totalHours,
    currentScore,
    targetScore,
    scoreGap,
    subjectScores,
    subjects: subjectResults.map(({ items, ...rest }) => rest),
    checklist,
    notTested: ranked.notTested,
    summary: {
      requiredCount: required.length,
      stretchCount: stretch.length,
      requiredHours: required.reduce((a, b) => a + (b.allocatedHours || 0), 0),
    },
  };
}
